import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { 
    getReferralLedger, 
    updateReferralLedger,
    completeReferral,
    cancelReferral,
    deleteReferralLedger
} from '../../actions/referralLedger';
import { Row, Col, Card, Button, Badge, Form, Alert, Modal, Spinner } from "react-bootstrap";

const ReferralLedger = (props) => {
    const { id } = useParams();
    const [formData, setFormData] = useState({
        status: '',
        notes: ''
    });
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (id && id !== 'new') {
            props.getReferralLedger(id);
        }
    }, [id]);

    useEffect(() => {
        if (props.currentReferralLedger) {
            setFormData({
                status: props.currentReferralLedger.status || '',
                notes: props.currentReferralLedger.notes || ''
            });
        }
    }, [props.currentReferralLedger]);

    // Clear messages after 5 seconds
    useEffect(() => {
        if (error || success) {
            const timer = setTimeout(() => {
                setError('');
                setSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, success]);

    const onChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setError('');
            await props.updateReferralLedger(id, formData);
            setShowUpdateModal(false);
            setSuccess('Referral ledger entry updated successfully!');
        } catch (err) {
            setError('Failed to update referral ledger entry. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleComplete = async () => {
        try {
            setIsSubmitting(true);
            setError('');
            await props.completeReferral(id, {
                triggerOrder: 'ORD123456', // This should come from a form or be dynamic
                deliveryMethod: 'home_delivery'
            });
            setShowCompleteModal(false);
            setSuccess('Referral completed successfully!');
        } catch (err) {
            setError('Failed to complete referral. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = async () => {
        try {
            setIsSubmitting(true);
            setError('');
            await props.cancelReferral(id, {
                reason: 'Admin cancelled referral'
            });
            setShowCancelModal(false);
            setSuccess('Referral cancelled successfully!');
        } catch (err) {
            setError('Failed to cancel referral. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        try {
            setIsSubmitting(true);
            setError('');
            await props.deleteReferralLedger(id);
            setShowDeleteModal(false);
            setSuccess('Referral ledger entry deleted successfully!');
            // Redirect to list after successful deletion
            setTimeout(() => {
                window.location.href = '/inventory/v1.2/referral-ledger';
            }, 2000);
        } catch (err) {
            setError('Failed to delete referral ledger entry. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { variant: 'warning', text: 'Pending' },
            completed: { variant: 'success', text: 'Completed' },
            cancelled: { variant: 'danger', text: 'Cancelled' },
            expired: { variant: 'secondary', text: 'Expired' }
        };
        
        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderReferralInfo = () => {
        const entry = props.currentReferralLedger;
        if (!entry) return null;

        return (
            <Card className="mb-4">
                <Card.Header className="bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-handshake me-2"></i>
                        Referral Information
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Referral ID</label>
                                <div><code className="fs-5">{entry.referralId}</code></div>
                            </div>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Status</label>
                                <div>{getStatusBadge(entry.status)}</div>
                            </div>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Created</label>
                                <div>{formatDate(entry.createdAt)}</div>
                            </div>
                            {entry.expiresAt && (
                                <div className="mb-3">
                                    <label className="fw-bold text-muted">Expires</label>
                                    <div>{formatDate(entry.expiresAt)}</div>
                                </div>
                            )}
                        </Col>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Reward Type</label>
                                <div className="text-capitalize">{entry.reward?.type || 'N/A'}</div>
                            </div>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Total Reward Value</label>
                                <div className="fs-5 text-success">{entry.reward?.totalValue || 0} cans</div>
                            </div>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Referrer Cans</label>
                                <div>{entry.reward?.referrerCans || 0}</div>
                            </div>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Referee Cans</label>
                                <div>{entry.reward?.refereeCans || 0}</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    const renderUserInfo = () => {
        const entry = props.currentReferralLedger;
        if (!entry) return null;

        return (
            <Row className="mb-4">
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-info text-white">
                            <h6 className="mb-0">
                                <i className="fas fa-user me-2"></i>
                                Referrer
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-2">
                                <label className="fw-bold text-muted">Name</label>
                                <div>{entry.referrer?.name || 'N/A'}</div>
                            </div>
                            <div className="mb-2">
                                <label className="fw-bold text-muted">Phone</label>
                                <div>{entry.referrer?.phone || 'N/A'}</div>
                            </div>
                            <div className="mb-2">
                                <label className="fw-bold text-muted">Email</label>
                                <div>{entry.referrer?.email || 'N/A'}</div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card className="h-100">
                        <Card.Header className="bg-success text-white">
                            <h6 className="mb-0">
                                <i className="fas fa-user-plus me-2"></i>
                                Referee
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-2">
                                <label className="fw-bold text-muted">Name</label>
                                <div>{entry.referee?.name || 'N/A'}</div>
                            </div>
                            <div className="mb-2">
                                <label className="fw-bold text-muted">Phone</label>
                                <div>{entry.referee?.phone || 'N/A'}</div>
                            </div>
                            <div className="mb-2">
                                <label className="fw-bold text-muted">Email</label>
                                <div>{entry.referee?.email || 'N/A'}</div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        );
    };

    const renderCompletionDetails = () => {
        const entry = props.currentReferralLedger;
        if (!entry || !entry.completionDetails) return null;

        return (
            <Card className="mb-4">
                <Card.Header className="bg-success text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-check-circle me-2"></i>
                        Completion Details
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Completed At</label>
                                <div>{formatDate(entry.completionDetails.completedAt)}</div>
                            </div>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Delivery Method</label>
                                <div className="text-capitalize">
                                    {entry.completionDetails.deliveryMethod?.replace('_', ' ') || 'N/A'}
                                </div>
                            </div>
                        </Col>
                        <Col md={6}>
                            {entry.completionDetails.triggerOrder && (
                                <div className="mb-3">
                                    <label className="fw-bold text-muted">Trigger Order</label>
                                    <div>
                                        <div>Order: {entry.completionDetails.triggerOrder.orderNumber}</div>
                                        <div>Amount: ₹{entry.completionDetails.triggerOrder.totalAmount}</div>
                                        <div>Status: {entry.completionDetails.triggerOrder.status}</div>
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    const renderMetadata = () => {
        const entry = props.currentReferralLedger;
        if (!entry || !entry.metadata) return null;

        return (
            <Card className="mb-4">
                <Card.Header className="bg-secondary text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-info-circle me-2"></i>
                        Metadata
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={6}>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Source</label>
                                <div className="text-capitalize">{entry.metadata.source || 'N/A'}</div>
                            </div>
                            <div className="mb-3">
                                <label className="fw-bold text-muted">Campaign ID</label>
                                <div>{entry.metadata.campaignId || 'N/A'}</div>
                            </div>
                        </Col>
                        <Col md={6}>
                            {entry.metadata.deviceInfo && (
                                <div className="mb-3">
                                    <label className="fw-bold text-muted">Device Info</label>
                                    <div>
                                        <div>Platform: {entry.metadata.deviceInfo.platform}</div>
                                        <div>Version: {entry.metadata.deviceInfo.version}</div>
                                    </div>
                                </div>
                            )}
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        );
    };

    const renderNotes = () => {
        const entry = props.currentReferralLedger;
        if (!entry) return null;

        return (
            <Card className="mb-4">
                <Card.Header className="bg-warning text-dark">
                    <h5 className="mb-0">
                        <i className="fas fa-sticky-note me-2"></i>
                        Notes
                    </h5>
                </Card.Header>
                <Card.Body>
                    <div className="mb-3">
                        <label className="fw-bold text-muted">Current Notes</label>
                        <div className="border p-3 rounded bg-light">
                            {entry.notes || 'No notes available'}
                        </div>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    const renderActions = () => {
        const entry = props.currentReferralLedger;
        if (!entry) return null;

        return (
            <Card className="mb-4">
                <Card.Header className="bg-dark text-white">
                    <h5 className="mb-0">
                        <i className="fas fa-cogs me-2"></i>
                        Actions
                    </h5>
                </Card.Header>
                <Card.Body>
                    <div className="d-flex gap-2 flex-wrap">
                        <Button
                            variant="primary"
                            onClick={() => setShowUpdateModal(true)}
                        >
                            <i className="fas fa-edit me-2"></i>
                            Update Entry
                        </Button>
                        
                        {entry.status === 'pending' && (
                            <>
                                <Button
                                    variant="success"
                                    onClick={() => setShowCompleteModal(true)}
                                >
                                    <i className="fas fa-check me-2"></i>
                                    Complete Referral
                                </Button>
                                <Button
                                    variant="warning"
                                    onClick={() => setShowCancelModal(true)}
                                >
                                    <i className="fas fa-times me-2"></i>
                                    Cancel Referral
                                </Button>
                            </>
                        )}
                        
                        <Button
                            variant="danger"
                            onClick={() => setShowDeleteModal(true)}
                        >
                            <i className="fas fa-trash me-2"></i>
                            Delete Entry
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    };

    if (props.loading) {
        return (
            <div className="d-flex justify-content-center align-items-center loading-container" style={{minHeight: '400px'}}>
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Loading referral ledger entry...</p>
                </div>
            </div>
        );
    }

    if (!props.currentReferralLedger) {
        return (
            <div className="container-fluid py-4">
                <div className="text-center py-5">
                    <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                    <h4>Referral Ledger Entry Not Found</h4>
                    <p className="text-muted">The requested referral ledger entry could not be found.</p>
                    <Link to="/inventory/v1.2/referral-ledger" className="btn btn-primary">
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Referral Ledger
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4" style={{background: '#f8fafc', minHeight: '100vh'}}>
                {/* Header */}
                <div className="mb-4">
                    <Link 
                        to="/inventory/v1.2/referral-ledger"
                        className="btn btn-outline-primary mb-3"
                    >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Referral Ledger
                    </Link>
                    <div>
                        <h2 className="text-primary mb-1">
                            <i className="fas fa-handshake me-2"></i>
                            Referral Ledger Entry
                        </h2>
                        <p className="text-muted mb-0">
                            View and manage referral ledger entry details
                        </p>
                    </div>
                </div>

                {/* Alert Messages */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')}>
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                        <i className="fas fa-check-circle me-2"></i>
                        {success}
                    </Alert>
                )}

                {/* Referral Information */}
                {renderReferralInfo()}

                {/* User Information */}
                {renderUserInfo()}

                {/* Completion Details */}
                {renderCompletionDetails()}

                {/* Metadata */}
                {renderMetadata()}

                {/* Notes */}
                {renderNotes()}

                {/* Actions */}
                {renderActions()}
            </div>

            {/* Update Modal */}
            <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)} centered>
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>
                        <i className="fas fa-edit me-2"></i>
                        Update Referral Ledger Entry
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpdate}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={formData.status}
                                onChange={onChange}
                                disabled={isSubmitting}
                            >
                                <option value="pending">Pending</option>
                                <option value="completed">Completed</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="expired">Expired</option>
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                name="notes"
                                value={formData.notes}
                                onChange={onChange}
                                disabled={isSubmitting}
                                placeholder="Enter notes..."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowUpdateModal(false)}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleUpdate}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Updating...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save me-2"></i>
                                Update Entry
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Complete Referral Modal */}
            <Modal show={showCompleteModal} onHide={() => setShowCompleteModal(false)} centered>
                <Modal.Header closeButton className="bg-success text-white">
                    <Modal.Title>
                        <i className="fas fa-check-circle me-2"></i>
                        Complete Referral
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="mb-3">
                        <i className="fas fa-handshake fa-3x text-success mb-3"></i>
                    </div>
                    <h5 className="mb-3">Are you sure you want to complete this referral?</h5>
                    <p className="text-muted mb-0">
                        This will mark the referral as completed and process the rewards.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowCompleteModal(false)}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={handleComplete}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Completing...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-check me-2"></i>
                                Complete Referral
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Cancel Referral Modal */}
            <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered>
                <Modal.Header closeButton className="bg-warning text-dark">
                    <Modal.Title>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Cancel Referral
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="mb-3">
                        <i className="fas fa-times-circle fa-3x text-warning mb-3"></i>
                    </div>
                    <h5 className="mb-3">Are you sure you want to cancel this referral?</h5>
                    <p className="text-muted mb-0">
                        This will mark the referral as cancelled and no rewards will be processed.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowCancelModal(false)}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                    </Button>
                    <Button 
                        variant="warning" 
                        onClick={handleCancel}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Cancelling...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-times me-2"></i>
                                Cancel Referral
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        Confirm Delete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="mb-3">
                        <i className="fas fa-trash-alt fa-3x text-danger mb-3"></i>
                    </div>
                    <h5 className="mb-3">Are you sure you want to delete this referral ledger entry?</h5>
                    <p className="text-muted mb-0">
                        This action cannot be undone. The entry will be permanently removed from the system.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowDeleteModal(false)}
                        disabled={isSubmitting}
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDelete}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-trash me-2"></i>
                                Delete Entry
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

ReferralLedger.propTypes = {
    getReferralLedger: PropTypes.func.isRequired,
    updateReferralLedger: PropTypes.func.isRequired,
    completeReferral: PropTypes.func.isRequired,
    cancelReferral: PropTypes.func.isRequired,
    deleteReferralLedger: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    currentReferralLedger: PropTypes.object
};

const mapStateToProps = state => ({
    loading: state.referralLedger.loading,
    currentReferralLedger: state.referralLedger.currentReferralLedger
});

export default connect(mapStateToProps, { 
    getReferralLedger, 
    updateReferralLedger,
    completeReferral,
    cancelReferral,
    deleteReferralLedger
})(ReferralLedger);
